import { apiClient } from './api'
import { Country, Currency } from '@/types'

export interface CryptoType {
  id: number
  code: string
  name: string
  min_payout: number
  is_active: boolean
}

export interface CountriesByRegion {
  [region: string]: Country[]
}

export interface CurrenciesByType {
  major: Currency[]
  minor: Currency[]
  crypto: Currency[]
}

export interface ApiResponse<T> {
  success: boolean
  data: T
  message: string
}

export interface UploadedFile {
  filename: string
  size: number
  type: string
  purpose: string
  url: string
  path: string
  uploaded_at: string
}

export interface UploadDocumentParams {
  file: File
  documentType: 'image' | 'document'
  purpose?: string
}

class HelpersService {
  private baseUrl = '/helpers'

  // Country methods
  async getCountries(): Promise<Country[]> {
    const response = await apiClient.get<any>(`${this.baseUrl}/countries/`)
    return response.results || response
  }

  async getCountry(isoCode: string): Promise<Country> {
    const response = await apiClient.get<Country>(`${this.baseUrl}/countries/${isoCode}/`)
    return response
  }

  async getCountriesByRegion(): Promise<CountriesByRegion> {
    const response = await apiClient.get<ApiResponse<CountriesByRegion>>(
      `${this.baseUrl}/countries/by-region/`
    )
    return response.data
  }

  async searchCountries(query: string): Promise<Country[]> {
    const response = await apiClient.get<ApiResponse<Country[]>>(
      `${this.baseUrl}/countries/search/`,
      {
        params: { q: query },
      }
    )
    return response.data
  }

  async getPopularCountries(): Promise<Country[]> {
    const response = await apiClient.get<ApiResponse<Country[]>>(
      `${this.baseUrl}/countries/popular/`
    )
    return response.data
  }

  // Currency methods
  async getCurrencies(): Promise<Currency[]> {
    const response = await apiClient.get<any>(`${this.baseUrl}/currencies/`)
    return response.results || response
  }

  async getCurrency(code: string): Promise<Currency> {
    const response = await apiClient.get<Currency>(`${this.baseUrl}/currencies/${code}/`)
    return response
  }

  async getCurrenciesByType(): Promise<CurrenciesByType> {
    const response = await apiClient.get<ApiResponse<CurrenciesByType>>(
      `${this.baseUrl}/currencies/by-type/`
    )
    return response.data
  }

  async searchCurrencies(query: string): Promise<Currency[]> {
    const response = await apiClient.get<ApiResponse<Currency[]>>(
      `${this.baseUrl}/currencies/search/`,
      {
        params: { q: query },
      }
    )
    return response.data
  }

  async getPopularCurrencies(): Promise<Currency[]> {
    const response = await apiClient.get<ApiResponse<Currency[]>>(
      `${this.baseUrl}/currencies/popular/`
    )
    return response.data
  }

  // Crypto types methods
  async getCryptoTypes(): Promise<CryptoType[]> {
    const response = await apiClient.get<any>(`${this.baseUrl}/crypto-types/`)
    return response.results || response
  }

  async getCryptoType(id: number): Promise<CryptoType> {
    const response = await apiClient.get<CryptoType>(`${this.baseUrl}/crypto-types/${id}/`)
    return response
  }

  async searchCryptoTypes(query: string): Promise<CryptoType[]> {
    const response = await apiClient.get<ApiResponse<CryptoType[]>>(
      `${this.baseUrl}/crypto-types/search/`,
      {
        params: { q: query },
      }
    )
    return response.data
  }

  async getActiveCryptoTypes(): Promise<CryptoType[]> {
    const response = await apiClient.get<ApiResponse<CryptoType[]>>(
      `${this.baseUrl}/crypto-types/active/`
    )
    return response.data
  }

  // Document upload methods
  async uploadDocument(params: UploadDocumentParams): Promise<UploadedFile> {
    const formData = new FormData()
    formData.append('file', params.file)
    formData.append('document_type', params.documentType)
    if (params.purpose) {
      formData.append('purpose', params.purpose)
    }

    const response = await apiClient.post<ApiResponse<UploadedFile>>(
      `${this.baseUrl}/upload/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  }

  async deleteDocument(filePath: string): Promise<{ deleted_path: string }> {
    const response = await apiClient.delete<ApiResponse<{ deleted_path: string }>>(
      `${this.baseUrl}/delete/`,
      {
        data: { file_path: filePath },
      }
    )
    return response.data
  }

  // Error handling
  handleError(error: any): string {
    if (error.response?.data?.message) {
      return error.response.data.message
    }
    if (error.response?.data?.detail) {
      return error.response.data.detail
    }
    if (error.message) {
      return error.message
    }
    return 'An unexpected error occurred'
  }
}

export const helpersService = new HelpersService()
